import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { MfitGuideText, ScreenBox } from "../../scenario/types";
import { audio } from "../../lib/audio";
import { activeTtsLang } from "../../lib/locale";
import type { DdCrit } from "./state";

export interface DdPerson { name: string; role: string; avatarUrl?: string; initials: string; tone: string }
export interface DdOpt { id: string; label: string; detail?: string }

/** Design canvas of every app: a fixed layout scaled into the monitor glass. */
export const LAND_W = 960;
export const LAND_H = 590;
export const PORT_W = 400;
export const PORT_H = 748;

export const MAYA_VOICE = "coral";
export const mayaOpts = () => ({ voice: MAYA_VOICE, lang: activeTtsLang() });

/** Decision groups the learner answers by picking an option; the chosen option
 *  always carries `.is-on`. */
const DECISION_GROUPS = ".dd-seg, .dd-choice, .dd-patches, .dd-checks, .dd-toggles, .dd1c-list";

/** Flag the first visible, still-unanswered decision group with `data-next`
 *  (it blinks, so the learner always sees where to click next). */
function markNextDecision(root: HTMLElement) {
  let found = false;
  root.querySelectorAll<HTMLElement>(DECISION_GROUPS).forEach((g) => {
    const isList = g.classList.contains("dd1c-list");
    const live = isList ? !!g.querySelector(".dd1c-arow button:not(:disabled)") : !!g.querySelector("button:not(:disabled)");
    const pending = live && !g.querySelector(".is-on") && g.offsetParent !== null;
    if (pending && !found) {
      found = true;
      if (!g.hasAttribute("data-next")) g.setAttribute("data-next", "");
    } else if (g.hasAttribute("data-next")) {
      g.removeAttribute("data-next");
    }
  });
}

/** Glue the app to the projected monitor glass and scale the design canvas
 *  into it. Falls back to a fixed left-of-centre box when the 3D scene is not
 *  rendering (capture mode, no box yet). */
export function Glass({ box, portrait, children }: { box: ScreenBox | null | undefined; portrait?: boolean; children: ReactNode }) {
  const outer = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  // Re-mark the next decision whenever a choice, tab or stage changes.
  useEffect(() => {
    const root = canvas.current;
    if (!root) return;
    let raf = 0;
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; markNextDecision(root); });
    };
    schedule();
    const mo = new MutationObserver(schedule);
    mo.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ["class", "disabled"] });
    return () => { mo.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, []);
  const W = portrait ? PORT_W : LAND_W;
  const H = portrait ? PORT_H : LAND_H;
  const glued = !!box && box.visible && box.widthPct > 6;
  const inset = 0.012;
  const style: CSSProperties = glued
    ? {
        left: `${box!.leftPct + box!.widthPct * inset}%`,
        top: `${box!.topPct + box!.heightPct * inset}%`,
        width: `${box!.widthPct * (1 - inset * 2)}%`,
        height: `${box!.heightPct * (1 - inset * 2)}%`,
      }
    : portrait
      ? { left: "16%", top: "6%", width: "28%", height: "88%" }
      : { left: "3%", top: "15%", width: "66%", height: "70%" };
  useLayoutEffect(() => {
    const el = outer.current;
    if (!el) return;
    const fit = () => {
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      setScale(Math.min(r.width / W, r.height / H));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener("resize", fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [W, H]);
  return (
    <div className="dd-root">
      <div ref={outer} className={`dd-glass${glued ? " is-glued" : ""}`} style={style}>
        <div ref={canvas} className={`dd-canvas${portrait ? " is-portrait" : ""}`} style={{ width: W, height: H, transform: `translate(-50%, -50%) scale(${scale})` }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/** App window chrome. */
export function Win({ app, tag, clock, children, tone }: { app: string; tag?: string; clock?: string; children: ReactNode; tone?: string }) {
  return (
    <div className={`dd-win${tone ? ` dd-win--${tone}` : ""}`}>
      <header className="dd-bar">
        <span className="dd-dots" aria-hidden="true"><i /><i /><i /></span>
        <span className="dd-app">{app}</span>
        {tag && <span className="dd-tag">{tag}</span>}
        <span className="dd-clock">{clock}</span>
      </header>
      <div className="dd-body">{children}</div>
    </div>
  );
}

export function MissionHead({ n, time, title, sub }: { n: number; time: string; title: string; sub?: string }) {
  return (
    <div className="dd-mhead">
      <span className="dd-kicker">Mission {n} of 10 {"\u00b7"} {time}</span>
      <h2>{title}</h2>
      {sub && <p>{sub}</p>}
    </div>
  );
}

export function Avatar({ p, size = 34 }: { p: DdPerson; size?: number }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="dd-av" style={{ width: size, height: size, background: p.tone, fontSize: Math.round(size * 0.36) }} aria-hidden="true">
      {p.avatarUrl && !broken ? <img src={p.avatarUrl} alt="" onError={() => setBroken(true)} draggable={false} /> : p.initials}
    </span>
  );
}

export function Bubble({ p, children, tone }: { p: DdPerson; children: ReactNode; tone?: "warn" | "ok" | "ai" }) {
  return (
    <div className={`dd-bubble${tone ? ` is-${tone}` : ""}`}>
      <Avatar p={p} size={30} />
      <div>
        <b>{p.name}</b> <span className="dd-role">{p.role}</span>
        <div className="dd-bubble-t">{children}</div>
      </div>
    </div>
  );
}

/** Segmented control: one neutral button per option, never hinting the answer. */
export function Seg({ options, value, onChange, disabled, small }: { options: DdOpt[]; value?: string; onChange: (id: string) => void; disabled?: boolean; small?: boolean }) {
  return (
    <div className={`dd-seg${small ? " is-small" : ""}`} role="radiogroup">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          className={value === o.id ? "is-on" : ""}
          disabled={disabled}
          onClick={() => {
            audio.click();
            onChange(o.id);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Neutral option cards (single choice). */
export function Choice({ options, value, onChange, disabled }: { options: DdOpt[]; value?: string; onChange: (id: string) => void; disabled?: boolean }) {
  return (
    <div className="dd-choice" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          className={`dd-opt${value === o.id ? " is-on" : ""}`}
          disabled={disabled}
          onClick={() => {
            audio.click();
            onChange(o.id);
          }}
        >
          <span className="dd-radio" aria-hidden="true" />
          <span>
            <b>{o.label}</b>
            {o.detail && <small>{o.detail}</small>}
          </span>
        </button>
      ))}
    </div>
  );
}

export function Btn({ children, onClick, primary, disabled, pulse, small }: { children: ReactNode; onClick: () => void; primary?: boolean; disabled?: boolean; pulse?: boolean; small?: boolean }) {
  return (
    <button
      type="button"
      className={`dd-btn${primary ? " is-primary" : ""}${small ? " is-small" : ""}${pulse && !disabled ? " cf-pulse-target" : ""}`}
      disabled={disabled}
      onClick={() => {
        audio.click();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

export function FixTag() {
  return <span className="dd-fixtag">Needs a fix</span>;
}

/** Amber "to decide" pill for a row the learner has not yet set. */
export function Todo({ label = "Decide" }: { label?: string }) {
  return <span className="dd-todo">{label}</span>;
}

/** Scored criteria. Every row expands to what was expected, why it matters
 *  and (where recorded) what the learner chose; the first miss starts open.
 *  `openAll` expands every missed row (used when the learner chooses "explain"). */
export function Rubric({ crits, title = "First attempt", openAll }: { crits: DdCrit[]; title?: string; openAll?: boolean }) {
  const got = crits.reduce((s, c) => s + (c.ok ? c.pts : 0), 0);
  const max = crits.reduce((s, c) => s + c.pts, 0);
  const [open, setOpen] = useState<string | null>(() => crits.find((c) => !c.ok)?.id ?? null);
  const isOpenFor = (c: DdCrit) => (openAll ? !c.ok : open === c.id);
  return (
    <div className="dd-rubric">
      <div className="dd-rubric-h">
        <span>{title}</span>
        <b>
          {got} / {max}
        </b>
      </div>
      <small className="dd-muted">Click a line for what was expected and why.</small>
      <ul>
        {crits.map((c) => {
          const isOpen = isOpenFor(c);
          return (
            <li key={c.id} className={`${c.ok ? "is-ok" : "is-miss"}${isOpen ? " is-open" : ""}`}>
              <button type="button" aria-expanded={isOpen} onClick={() => { audio.click(); setOpen(isOpen ? null : c.id); }}>
                <span className="dd-mark" aria-hidden="true">{c.ok ? "\u2713" : "\u2715"}</span>
                <span className="dd-sr">{c.ok ? "Met: " : "Not met: "}</span>
                <span className="dd-rl">{c.label}</span>
                <em>{c.pts} pts {isOpen ? "\u25B4" : "\u25BE"}</em>
              </button>
              {isOpen && (c.expect || c.why || c.yours) && (
                <div className="dd-rdetail">
                  {c.yours && <p><b>Your choice:</b> {c.yours}</p>}
                  {c.expect && <p><b>Expected:</b> {c.expect}</p>}
                  {c.why && <p><b>Why:</b> {c.why}</p>}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Result footer. When everything is right, one Continue. When not, the learner
 *  chooses: try again to improve the score (enter the fix flow), or keep this
 *  result and have the tutor explain what was missed. The first-attempt score
 *  is already saved either way. */
export function MissFooter({ allOk, explained, onExplain, onRetry, onContinue, retryLabel = "Try again to improve your score" }: {
  allOk: boolean; explained: boolean; onExplain: () => void; onRetry: () => void; onContinue: () => void; retryLabel?: string;
}) {
  if (allOk) return <Btn primary pulse onClick={onContinue}>Continue</Btn>;
  if (explained)
    return (
      <>
        <span className="dd-kept">Result saved as it is. You can see it again in your 17:00 replay.</span>
        <Btn primary pulse onClick={onContinue}>Continue</Btn>
      </>
    );
  return (
    <>
      <span className="dd-muted">Not everything is right yet. Your decisions are already recorded.</span>
      <Btn onClick={onExplain}>Keep this result &amp; explain</Btn>
      <Btn primary pulse onClick={onRetry}>{retryLabel}</Btn>
    </>
  );
}

/** Escape from a fix flow: back to the result, keeping the first attempt. */
export function KeepInstead({ onClick }: { onClick: () => void }) {
  return <Btn onClick={onClick}>Keep my first result instead</Btn>;
}

export interface DdIntroData {
  n: number;
  time: string;
  title: string;
  situation: string;
  risk: string;
  safe: string[];
  steps: string[];
  terms?: { term: string; meaning: string }[];
}

/** The mission briefing shown before each exercise. */
export function MissionIntro({ intro, portrait, onStart, onGuideChange }: { intro: DdIntroData; portrait?: boolean; onStart: () => void; onGuideChange?: (g: MfitGuideText | null) => void }) {
  useEffect(() => {
    onGuideChange?.({ title: `${intro.time} \u00b7 Mission ${intro.n}`, body: "Read what this mission is about and what safe handling looks like, then click Start the exercise.", actionLabel: "Start the exercise" });
  }, [intro, onGuideChange]);
  const [safeOpen, setSafeOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const terms = intro.terms ?? [];
  const chevron = (open: boolean) => (
    <svg className={`dd-ib-chev${open ? " is-open" : ""}`} viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M3.5 6l4.5 4.5L12.5 6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
  );
  /** Bold lead-in of a step: "In the ..." up to the first comma, else the first word. */
  const stepLead = (s: string): [string, string] => {
    const m = /^In the [^,]+,/.exec(s);
    if (m) return [m[0], s.slice(m[0].length)];
    const i = s.indexOf(" ");
    return i < 0 ? [s, ""] : [s.slice(0, i), s.slice(i)];
  };
  return (
    <div className={`dd-intro${portrait ? " is-portrait" : ""}`}>
      <div className="dd-intro-scroll">
        <header className="dd-ib-head">
          <span className="dd-kicker">Mission {intro.n} of 10 {"\u00b7"} {intro.time} {"\u00b7"} Before you start</span>
          <h2>{intro.title}</h2>
        </header>
        <section className="dd-ib-sec">
          <h3 className="dd-ib-h">The situation</h3>
          <p>{intro.situation}</p>
        </section>
        <section className="dd-ib-risk">
          <svg className="dd-ib-warn" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M10 2.2L18.6 17H1.4L10 2.2z" fill="currentColor" /><path d="M10 7.6v4.2M10 14.1v.1" stroke="#241a08" strokeWidth="1.7" strokeLinecap="round" /></svg>
          <div>
            <b className="dd-ib-risk-h">The risk</b>
            <p>{intro.risk}</p>
          </div>
        </section>
        <section className="dd-ib-sec">
          <h3 className="dd-ib-h">Your task</h3>
          <ol className="dd-ib-steps">
            {intro.steps.map((s, i) => {
              const [lead, rest] = stepLead(s);
              return (
                <li key={s}>
                  <span className="dd-ib-num">{i + 1}</span>
                  <span><b>{lead}</b>{rest}</span>
                </li>
              );
            })}
          </ol>
        </section>
        <div className="dd-ib-acc">
          <div className={`dd-ib-row${safeOpen ? " is-open" : ""}`}>
            <button type="button" className="dd-ib-rowhead" aria-expanded={safeOpen} onClick={() => setSafeOpen((o) => !o)}>
              <svg className="dd-ib-ico" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><rect x="2.5" y="2.5" width="15" height="15" rx="3" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M6.4 10.2l2.4 2.4 4.8-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <b>Safe handling checklist</b>
              <small>{intro.safe.length} {intro.safe.length === 1 ? "check" : "checks"}</small>
              {chevron(safeOpen)}
            </button>
            {safeOpen && (
              <ul className="dd-ib-safe">{intro.safe.map((s) => <li key={s}>{s}</li>)}</ul>
            )}
          </div>
          {terms.length > 0 && (
            <div className={`dd-ib-row${termsOpen ? " is-open" : ""}`}>
              <button type="button" className="dd-ib-rowhead" aria-expanded={termsOpen} onClick={() => setTermsOpen((o) => !o)}>
                <svg className="dd-ib-ico" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M10 5.2C8.6 4.1 6.4 3.7 3 3.9v11c3.4-.2 5.6.2 7 1.3 1.4-1.1 3.6-1.5 7-1.3v-11c-3.4-.2-5.6.2-7 1.3zM10 5.2v11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
                <b>Key terms</b>
                <small>{terms.map((t) => t.term).join(" \u00b7 ")}</small>
                {chevron(termsOpen)}
              </button>
              {termsOpen && (
                <dl className="dd-terms dd-ib-terms">
                  {terms.map((t) => (
                    <div key={t.term}><dt>{t.term}</dt><dd>{t.meaning}</dd></div>
                  ))}
                </dl>
              )}
            </div>
          )}
        </div>
      </div>
      <footer className="dd-foot">
        <small className="dd-muted">Your first committed decision is scored. You can explore, open details and change selections freely before that.</small>
        <Btn primary pulse onClick={onStart}>Start the exercise</Btn>
      </footer>
    </div>
  );
}

/** Maya's portrait and line. Speaks once on mount (pre-rendered voice). */
export function Maya({ p, text, speak = true, compact }: { p: DdPerson; text: string; speak?: boolean; compact?: boolean }) {
  const [talking, setTalking] = useState(false);
  useEffect(() => {
    if (!speak) return;
    let alive = true;
    setTalking(true);
    void audio.speak(text, mayaOpts()).finally(() => {
      if (alive) setTalking(false);
    });
    return () => {
      alive = false;
    };
  }, [text, speak]);
  return (
    <div className={`dd-maya${compact ? " is-compact" : ""}`}>
      <Avatar p={p} size={compact ? 38 : 48} />
      <div>
        <div className="dd-maya-h">
          <b>{p.name}</b> <span className="dd-role">{p.role}</span>
          {talking && (
            <span className="dd-bars" aria-label="Speaking">
              <i />
              <i />
              <i />
              <i />
            </span>
          )}
        </div>
        <p>{text}</p>
      </div>
    </div>
  );
}

/** Drive the shared top-right guide from a stage map. */
export function useGuide(onGuideChange: ((g: MfitGuideText | null) => void) | undefined, map: Record<string, MfitGuideText> | undefined, stage: string) {
  useEffect(() => {
    onGuideChange?.(map?.[stage] ?? null);
  }, [onGuideChange, map, stage]);
  useEffect(() => () => onGuideChange?.(null), [onGuideChange]);
}

/** Prefetch every line a device may speak so the voice prerender records it. */
export function usePrefetch(lines: (string | undefined)[]) {
  const key = lines.join("|");
  useEffect(() => {
    for (const l of lines) if (l) void audio.prefetchSpeech(l, mayaOpts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export function Code({ children, small }: { children: string; small?: boolean }) {
  return <pre className={`dd-code${small ? " is-small" : ""}`}>{children}</pre>;
}

export function Diff({ lines }: { lines: string[] }) {
  return (
    <pre className="dd-code dd-diff">
      {lines.map((l, i) => (
        <span key={i} className={l.startsWith("+") ? "is-add" : l.startsWith("-") ? "is-del" : ""}>
          {l.startsWith("+") ? <i className="dd-sr">added: </i> : l.startsWith("-") ? <i className="dd-sr">removed: </i> : null}
          {l}
          {"\n"}
        </span>
      ))}
    </pre>
  );
}

/** Small "What would have happened" box: visibly separate from the real timeline. */
export function WhatIf({ children }: { children: ReactNode }) {
  return (
    <div className="dd-whatif">
      <span>What would have happened (reconstruction, not your timeline)</span>
      <p>{children}</p>
    </div>
  );
}

export function Consequence({ tone, title, children }: { tone: "risk" | "ok" | "warn" | "info"; title: string; children?: ReactNode }) {
  return (
    <div className={`dd-cons is-${tone}`} role="status">
      <b>{title}</b>
      {children && <div className="dd-cons-t">{children}</div>}
    </div>
  );
}

/** One outcome line of a mission result (what happened, with its tone). */
export interface DdResultOutcome { tone: "risk" | "ok" | "warn" | "info"; title: string; body?: ReactNode }
interface ReviewRow { item: string; yours: string; expect: string; bad: boolean }

const normTxt = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const splitPair = (s: string): [string, string] | null => {
  const i = s.indexOf(": ");
  return i > 0 ? [s.slice(0, i).trim(), s.slice(i + 2).trim()] : null;
};

/** The Item / Your choice / Expected rows of one criterion. `yours` of the form
 *  "Item: choice; Item: choice" becomes one row per item when an expected value
 *  is known for every item (from `known`, or from "Item: value" parts of `expect`);
 *  otherwise a single row holds the whole recorded choice and expectation. */
function reviewRows(c: DdCrit, known?: Record<string, string>): ReviewRow[] {
  const expMap: Record<string, string> = { ...(known ?? {}) };
  for (const seg of (c.expect ?? "").split(/;\s*/)) {
    const p = splitPair(seg);
    if (p && expMap[p[0]] === undefined) expMap[p[0]] = p[1];
  }
  const pairs = (c.yours ?? "").split(/;\s+/).filter(Boolean).map(splitPair);
  if (pairs.length > 1 || (pairs.length === 1 && pairs[0])) {
    if (pairs.every((p) => p && expMap[p[0]] !== undefined)) {
      return (pairs as [string, string][]).map(([item, yours]) => {
        const expect = expMap[item];
        const match = normTxt(expect).includes(normTxt(yours)) || normTxt(yours).includes(normTxt(expect));
        return { item: item.charAt(0).toUpperCase() + item.slice(1), yours, expect, bad: !c.ok && !match };
      });
    }
  }
  return [{ item: c.label, yours: c.yours ?? (c.ok ? "As expected" : "Not as expected"), expect: c.expect ?? "-", bad: !c.ok }];
}

function RsIcon({ kind }: { kind: "ok" | "miss" | "chev" | "left" | "right" | "people" | "info" }) {
  const common = { viewBox: "0 0 20 20", width: 16, height: 16, fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  if (kind === "ok") return <svg {...common}><path d="M5 10.5l3.2 3.2L15 7" /></svg>;
  if (kind === "miss") return <svg {...common}><path d="M6.5 6.5l7 7M13.5 6.5l-7 7" /></svg>;
  if (kind === "chev") return <svg {...common}><path d="M5 7.5l5 5 5-5" /></svg>;
  if (kind === "left") return <svg {...common}><path d="M12 5l-5 5 5 5" /></svg>;
  if (kind === "right") return <svg {...common}><path d="M8 5l5 5-5 5" /></svg>;
  if (kind === "people") return <svg {...common}><circle cx="7.5" cy="7.5" r="2.4" /><path d="M3 15.5c.4-2.6 2.2-3.8 4.5-3.8s4.1 1.2 4.5 3.8" /><circle cx="14" cy="8.2" r="1.9" /><path d="M13.6 12c2 0 3.1 1 3.4 3" /></svg>;
  return <svg {...common}><circle cx="10" cy="10" r="7" /><path d="M10 9v4.2M10 6.6v.1" /></svg>;
}

/** The shared result screen of every mission: score band, score breakdown with
 *  a review pane for the selected check, and two collapsible notes (assistant /
 *  outcome and the tutor). Fits the 960x590 canvas without scrolling. `footer`
 *  carries the mission's own actions (MissFooter or a Continue button). */
export function MissionResult({ n, title, task, contextChip, onTaskDetails, scoreTitle = "First attempt", crits, assistant, maya, outcomes, outcomeLabel, outcomeSub, more, gate, expected, footer }: {
  n: number; time?: string; title: string; task?: string;
  contextChip?: string; onTaskDetails?: () => void;
  scoreTitle?: string;
  crits: DdCrit[];
  assistant?: { person: DdPerson; text: string; tone?: "warn" | "ok" | "ai" };
  maya?: { person: DdPerson; text: string; speak?: boolean };
  /** What happened, one line per outcome; shown in the first collapsible row. */
  outcomes?: DdResultOutcome[];
  outcomeLabel?: string;
  /** Sub-line of the first collapsible row when there is no outcome title to show. */
  outcomeSub?: string;
  /** Extra mission evidence for the first collapsible row. */
  more?: ReactNode;
  /** An action the learner must take before continuing; always visible. */
  gate?: ReactNode;
  /** Known expected value per criterion id and "Item" name, for the review table. */
  expected?: Record<string, Record<string, string>>;
  footer: ReactNode;
}) {
  const got = crits.reduce((s, c) => s + (c.ok ? c.pts : 0), 0);
  const max = crits.reduce((s, c) => s + c.pts, 0);
  const missed = crits.filter((c) => !c.ok);
  const reviewSet = missed.length ? missed : crits;
  const [sel, setSel] = useState<string>(() => reviewSet[0]?.id ?? "");
  const [dock, setDock] = useState<"a" | "b" | null>(null);
  const cur = crits.find((c) => c.id === sel) ?? reviewSet[0];
  const ri = cur ? reviewSet.findIndex((c) => c.id === cur.id) : -1;
  const inSet = ri >= 0;
  const pick = (id: string) => {
    audio.click();
    setSel(id);
  };
  const prev = inSet ? reviewSet[ri - 1] : reviewSet[reviewSet.length - 1];
  const next = inSet ? reviewSet[ri + 1] : reviewSet[0];
  const hasA = !!assistant || !!outcomes?.length || !!more;
  const toggle = (k: "a" | "b") => {
    audio.click();
    setDock((d) => (d === k ? null : k));
  };
  const rows = cur ? reviewRows(cur, expected?.[cur.id]) : [];
  const subOf = (c: DdCrit) => c.yours ?? (c.ok ? "Met as expected" : "Needs review");
  const labelA = assistant ? "Assistant response" : outcomeLabel ?? "What happened";
  const subA = assistant ? `${assistant.person.name} \u00b7 ${assistant.person.role}` : outcomes?.[0]?.title ?? outcomeSub ?? "";
  return (
    <div className={`dd-rs${gate ? " has-gate" : ""}`}>
      <header className="dd-rs-head">
        <div className="dd-rs-title">
          <span className="dd-kicker">Mission {n} of 10 {"\u00b7"} Results</span>
          <h2>{title}</h2>
          {task && <p><b>Task:</b> {task}</p>}
        </div>
        {(contextChip || onTaskDetails) && (
          <div className="dd-rs-side">
            {contextChip && <span className="dd-rs-chip">{contextChip}</span>}
            {onTaskDetails && (
              <button type="button" className="dd-rs-link" onClick={() => { audio.click(); onTaskDetails(); }}>
                Task details <RsIcon kind="right" />
              </button>
            )}
          </div>
        )}
      </header>

      <div className="dd-rs-band">
        <b className="dd-rs-score">{got} / {max}</b>
        <span className="dd-rs-first">{scoreTitle}</span>
        <span className="dd-rs-sep" aria-hidden="true" />
        <span className="dd-rs-counts">{crits.length - missed.length} {crits.length - missed.length === 1 ? "check" : "checks"} met {"\u00b7"} {missed.length} to review</span>
        <span className="dd-rs-savedtag">Saved result</span>
      </div>

      <div className="dd-rs-cols">
        <section className="dd-rs-left" aria-label="Score breakdown">
          <h3>Score breakdown</h3>
          <small className="dd-rs-hint">Select a check to see the explanation.</small>
          <ul className="dd-rs-list">
            {crits.map((c) => (
              <li key={c.id}>
                <button type="button" className={`dd-rs-item ${c.ok ? "is-ok" : "is-miss"}${cur?.id === c.id ? " is-sel" : ""}`} aria-pressed={cur?.id === c.id} onClick={() => pick(c.id)}>
                  <span className="dd-rs-mark" aria-hidden="true"><RsIcon kind={c.ok ? "ok" : "miss"} /></span>
                  <span className="dd-rs-lt">
                    <span className="dd-sr">{c.ok ? "Met: " : "Not met: "}</span>
                    <b title={c.label}>{c.label}</b>
                    <small className="dd-rs-sub" title={subOf(c)}>{subOf(c)}</small>
                  </span>
                  <span className="dd-rs-pts">{c.ok ? c.pts : 0} / {c.pts}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        {cur && (
          <section className="dd-rs-right" aria-label="Review">
            <div className="dd-rs-rtop">
              <span>{missed.length ? (inSet ? `Review item ${ri + 1} of ${reviewSet.length}` : `Check ${crits.indexOf(cur) + 1} of ${crits.length}`) : `Check ${crits.indexOf(cur) + 1} of ${crits.length}`}</span>
              <span className="dd-rs-arrows">
                <button type="button" aria-label="Previous review item" disabled={!prev} onClick={() => prev && pick(prev.id)}><RsIcon kind="left" /></button>
                <button type="button" aria-label="Next review item" disabled={!next} onClick={() => next && pick(next.id)}><RsIcon kind="right" /></button>
              </span>
            </div>
            <div className="dd-rs-rhead">
              <h3>{cur.label}</h3>
              <span className={`dd-rs-state ${cur.ok ? "is-ok" : "is-miss"}`}>{cur.ok ? `Met \u00b7 ${cur.pts} / ${cur.pts}` : `Not met \u00b7 0 / ${cur.pts}`}</span>
            </div>
            <div className="dd-rs-rbody">
              <table className="dd-rs-table">
                <thead>
                  <tr><th>Item</th><th>Your choice</th><th>Expected</th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.item}>
                      <td>{r.item}</td>
                      <td className={r.bad ? "is-bad" : ""}>{r.yours}</td>
                      <td className={r.bad ? "is-bad" : ""}>{r.expect}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {cur.why && (
                <div className="dd-rs-why">
                  <b>Why it matters</b>
                  <p>{cur.why}</p>
                </div>
              )}
            </div>
            {inSet && next && missed.length > 0 && (
              <button type="button" className="dd-rs-nextrow" onClick={() => pick(next.id)}>
                <span>Next to review: {next.label}</span>
                <RsIcon kind="right" />
              </button>
            )}
          </section>
        )}
      </div>

      {gate && <div className="dd-rs-gate">{gate}</div>}

      {(hasA || maya) && (
        <div className="dd-rs-dock">
          {hasA && (
            <div className="dd-rs-pop" hidden={dock !== "a"}>
              {outcomes?.map((o) => (
                <Consequence key={o.title} tone={o.tone} title={o.title}>{o.body}</Consequence>
              ))}
              {assistant && <Bubble p={assistant.person} tone={assistant.tone ?? "ai"}>{assistant.text}</Bubble>}
              {more}
            </div>
          )}
          {maya && (
            <div className="dd-rs-pop" hidden={dock !== "b"}>
              <Maya p={maya.person} text={maya.text} speak={maya.speak} compact />
            </div>
          )}
          <div className="dd-rs-rows">
            {hasA && (
              <button type="button" className={`dd-rs-acc${dock === "a" ? " is-open" : ""}`} aria-expanded={dock === "a"} onClick={() => toggle("a")}>
                <span className="dd-rs-aico">{assistant ? "AI" : <RsIcon kind="info" />}</span>
                <span className="dd-rs-at"><b>{labelA}</b><small>{subA}</small></span>
                <span className="dd-rs-chev"><RsIcon kind="chev" /></span>
              </button>
            )}
            {maya && (
              <button type="button" className={`dd-rs-acc${dock === "b" ? " is-open" : ""}`} aria-expanded={dock === "b"} onClick={() => toggle("b")}>
                <span className="dd-rs-aico is-people"><RsIcon kind="people" /></span>
                <span className="dd-rs-at"><b>{maya.person.name}{"\u2019"}s advice</b><small>{maya.person.name} {"\u00b7"} {maya.person.role}</small></span>
                <span className="dd-rs-chev"><RsIcon kind="chev" /></span>
              </button>
            )}
          </div>
        </div>
      )}

      <footer className="dd-rs-foot">
        <span className="dd-rs-saved">Result saved as is. You can see it again in your 17:00 replay.</span>
        <div className="dd-rs-act">{footer}</div>
      </footer>
    </div>
  );
}
